import React, { useState, useEffect, useCallback } from "react";
import { getDatabase, ref, get, set } from "firebase/database";
import { app } from "./firebase/config";
import "./AdminDashboard.css";
import AdminLayout from "./AdminLayout";
import AdminUserManagement from "./AdminUserManagement";
import AdminErrorManagement from "./AdminErrorManagement";
import AdminApiManagement from "./AdminApiManagement";
import AdminPriceAnalysisManagement from "./AdminPriceAnalysisManagement";
import AdminNewsGenerator from "./AdminNewsGenerator";
import AdminManusKeyManager from "./AdminManusKeyManager";

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem("adminAuthenticated") === "true";
  });
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalTransactions: 0,
    totalRevenue: 0,
  });
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);

  // Xử lý thay đổi tab
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Định dạng số tiền
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  // Định dạng thời gian
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleDateString("vi-VN", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Lấy hoạt động gần đây
  const getRecentActivity = useCallback(() => {
    const activities = [];

    // Thêm người dùng mới
    users.slice(0, 3).forEach((user) => {
      activities.push({
        id: `user-${user.id}`,
        type: "user",
        message: `Người dùng mới: ${
          user.profile?.displayName || user.displayName || "N/A"
        }`,
        timestamp: user.createdAt,
        icon: "user-plus",
      });
    });

    // Thêm giao dịch mới
    transactions.slice(0, 3).forEach((transaction) => {
      activities.push({
        id: `transaction-${transaction.id}`,
        type: "transaction",
        message: `Giao dịch mới: ${formatCurrency(transaction.amount)}`,
        timestamp: transaction.updatedAt,
        icon: "credit-card",
      });
    });

    // Sắp xếp theo thời gian
    return activities
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
      .slice(0, 5);
  }, [users, transactions]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    // Check for admin password - default to "2025@@" if not set in env
    const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD || "2025@@";
    if (password === adminPassword) {
      localStorage.setItem("adminAuthenticated", "true");
      setIsAuthenticated(true);
    } else {
      setErrorMsg("Mật khẩu không đúng");
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return; // Không fetch dữ liệu nếu chưa xác thực

    // Lấy trạng thái maintenance mode
    const fetchMaintenanceMode = async () => {
      try {
        const db = getDatabase(app);
        const maintenanceModeRef = ref(db, "system/maintenanceMode");
        const snapshot = await get(maintenanceModeRef);
        if (snapshot.exists()) {
          setMaintenanceMode(snapshot.val());
        }
      } catch (error) {
        console.error("Error fetching maintenance mode:", error);
      }
    };

    fetchMaintenanceMode();

    const fetchData = async () => {
      setLoading(true);
      setError(null); // Reset error state
      try {
        const db = getDatabase(app);

        // Lấy dữ liệu người dùng
        const usersRef = ref(db, "users");
        const usersSnapshot = await get(usersRef);
        const usersData = usersSnapshot.val();

        if (usersData) {
          const usersArray = Object.entries(usersData).map(([id, data]) => {
            // Lấy email và displayName từ profile
            const email = data.profile?.email || data.email || "";
            const displayName =
              data.profile?.displayName || data.displayName || "";

            return {
              id,
              ...data,
              email: email,
              displayName: displayName,
            };
          });

          // Sắp xếp người dùng theo thời gian từ mới đến cũ
          const sortedUsers = usersArray.sort((a, b) => {
            return (b.createdAt || 0) - (a.createdAt || 0);
          });

          setUsers(sortedUsers);

          // Tính toán số liệu người dùng
          setStats((prev) => ({
            ...prev,
            totalUsers: usersArray.length,
            activeUsers: usersArray.filter((user) => user.status === "active")
              .length,
          }));
        } else {
          setUsers([]);
          setStats((prev) => ({ ...prev, totalUsers: 0, activeUsers: 0 }));
        }

        // Lấy dữ liệu giao dịch từ payment-history
        const transactionsRef = ref(db, "payment-history");
        const transactionsSnapshot = await get(transactionsRef);
        const transactionsData = transactionsSnapshot.val();

        if (transactionsData) {
          const transactionsArray = Object.entries(transactionsData).map(
            ([id, data]) => ({
              id,
              ...data,
            })
          );

          // Sắp xếp giao dịch theo thời gian từ mới đến cũ
          const sortedTransactions = transactionsArray.sort((a, b) => {
            return (b.updatedAt || 0) - (a.updatedAt || 0);
          });

          setTransactions(sortedTransactions);

          // Tính tổng doanh thu và số giao dịch
          const totalRevenue = transactionsArray.reduce(
            (sum, transaction) =>
              transaction.status === "COMPLETED" ||
              transaction.status === "PAID"
                ? sum + (Number(transaction.amount) || 0)
                : sum,
            0
          );

          setStats((prev) => ({
            ...prev,
            totalTransactions: transactionsArray.length,
            totalRevenue: totalRevenue,
          }));
        } else {
          setTransactions([]);
          setStats((prev) => ({
            ...prev,
            totalTransactions: 0,
            totalRevenue: 0,
          }));
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Có lỗi khi tải dữ liệu. Vui lòng thử lại sau.");
        // Reset states on error
        setUsers([]);
        setTransactions([]);
        setStats({
          totalUsers: 0,
          activeUsers: 0,
          totalTransactions: 0,
          totalRevenue: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated]); // Removed getRecentActivity from dependency array

  // Cập nhật hoạt động gần đây khi dữ liệu thay đổi
  useEffect(() => {
    if (users.length > 0 || transactions.length > 0) {
      setRecentActivity(getRecentActivity());
    }
  }, [users, transactions, getRecentActivity]);

  if (!isAuthenticated) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-card">
          <div className="login-header">
            <h1 className="login-title">Admin Dashboard</h1>
            <p className="login-subtitle">Vui lòng đăng nhập để tiếp tục</p>
          </div>
          <form onSubmit={handlePasswordSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Mật khẩu
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Nhập mật khẩu admin"
                className="form-input"
                required
              />
            </div>
            {errorMsg && <div className="error-message">{errorMsg}</div>}
            <button type="submit" className="login-button">
              <svg
                className="login-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                />
              </svg>
              Đăng nhập
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <AdminLayout activeTab={activeTab} onTabChange={handleTabChange}>
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <div className="loading-text">
              <h3>Đang tải dữ liệu...</h3>
              <p>Vui lòng chờ trong giây lát</p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout activeTab={activeTab} onTabChange={handleTabChange}>
        <div className="error-container">
          <div className="error-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h3 className="error-title">Có lỗi xảy ra</h3>
          <p className="error-message">{error}</p>
          <button
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Thử lại
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeTab={activeTab} onTabChange={handleTabChange}>
      {activeTab === "dashboard" && (
        <div className="dashboard-container">
          {/* Header với Welcome Message */}
          <div className="dashboard-header">
            <div className="header-content">
              <h1 className="dashboard-title">Chào mừng trở lại! 👋</h1>
              <p className="dashboard-subtitle">
                Tổng quan về hoạt động hệ thống hôm nay
              </p>
            </div>
            <div className="header-actions">
              <button
                className="action-button refresh-button"
                onClick={() => window.location.reload()}
              >
                <svg
                  className="button-icon"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Làm mới
              </button>
            </div>
          </div>

          {/* Statistics Grid với Animation */}
          <div className="stats-grid enhanced">
            <div className="stat-card users-card enhanced">
              <div className="stat-card-header">
                <div className="stat-card-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m-7.5-2.962A3.375 3.375 0 019 12.25c0-1.03.394-1.983 1.057-2.753m1.943-1.943A3.375 3.375 0 0115 6.25c1.03 0 1.983.394 2.753 1.057m-4.682 4.682a3.375 3.375 0 01-4.682 0M19.5 12a7.5 7.5 0 11-15 0 7.5 7.5 0 0115 0z"
                    />
                  </svg>
                </div>
                <div className="stat-card-info">
                  <p className="stat-card-title">Tổng số người dùng</p>
                  <p className="stat-card-value">
                    {stats.totalUsers.toLocaleString()}
                  </p>
                  <div className="stat-card-trend positive">
                    <svg
                      className="trend-icon"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                    <span>+12% so với tháng trước</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="stat-card active-users-card enhanced">
              <div className="stat-card-header">
                <div className="stat-card-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                    />
                  </svg>
                </div>
                <div className="stat-card-info">
                  <p className="stat-card-title">Người dùng hoạt động</p>
                  <p className="stat-card-value">
                    {stats.activeUsers.toLocaleString()}
                  </p>
                  <div className="stat-card-trend positive">
                    <svg
                      className="trend-icon"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                    <span>+8% so với tháng trước</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="stat-card transactions-card enhanced">
              <div className="stat-card-header">
                <div className="stat-card-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3.375m-3.375 2.25h10.5m4.5-9.75v10.125c0 .621-.504 1.125-1.125 1.125H3.375c-.621 0-1.125-.504-1.125-1.125V8.25m17.25-3.75h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 21h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5z"
                    />
                  </svg>
                </div>
                <div className="stat-card-info">
                  <p className="stat-card-title">Tổng số giao dịch</p>
                  <p className="stat-card-value">
                    {stats.totalTransactions.toLocaleString()}
                  </p>
                  <div className="stat-card-trend positive">
                    <svg
                      className="trend-icon"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                    <span>+15% so với tháng trước</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="stat-card revenue-card enhanced">
              <div className="stat-card-header">
                <div className="stat-card-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="stat-card-info">
                  <p className="stat-card-title">Tổng doanh thu</p>
                  <p className="stat-card-value">
                    {formatCurrency(stats.totalRevenue)}
                  </p>
                  <div className="stat-card-trend positive">
                    <svg
                      className="trend-icon"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                    <span>+23% so với tháng trước</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions">
            <h3 className="section-title">Thao tác nhanh</h3>
            <div className="actions-grid">
              <button
                className="action-card"
                onClick={() => handleTabChange("users")}
              >
                <div className="action-icon users">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                    />
                  </svg>
                </div>
                <div className="action-content">
                  <h4>Quản lý người dùng</h4>
                  <p>Xem và quản lý tài khoản người dùng</p>
                </div>
              </button>

              <button
                className="action-card"
                onClick={() => handleTabChange("api-history")}
              >
                <div className="action-icon api">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
                <div className="action-content">
                  <h4>Lịch sử API</h4>
                  <p>Theo dõi các cuộc gọi API</p>
                </div>
              </button>

              <button
                className="action-card"
                onClick={() => handleTabChange("settings")}
              >
                <div className="action-icon settings">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <div className="action-content">
                  <h4>Cài đặt hệ thống</h4>
                  <p>Cấu hình và bảo trì hệ thống</p>
                </div>
              </button>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="dashboard-grid">
            {/* Recent Activity */}
            <div className="dashboard-card activity-card">
              <div className="card-header">
                <h3 className="card-title">
                  <svg
                    className="title-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  Hoạt động gần đây
                </h3>
              </div>
              <div className="activity-list">
                {recentActivity.length > 0 ? (
                  recentActivity.map((activity) => (
                    <div key={activity.id} className="activity-item">
                      <div className={`activity-icon ${activity.type}`}>
                        {activity.icon === "user-plus" && (
                          <svg
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                            />
                          </svg>
                        )}
                        {activity.icon === "credit-card" && (
                          <svg
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="activity-content">
                        <p className="activity-message">{activity.message}</p>
                        <p className="activity-time">
                          {formatDate(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <svg
                      className="empty-icon"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                      />
                    </svg>
                    <p>Chưa có hoạt động nào</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Users */}
            <div className="dashboard-card users-card-table">
              <div className="card-header">
                <h3 className="card-title">
                  <svg
                    className="title-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                    />
                  </svg>
                  Người dùng mới
                </h3>
                <button
                  className="view-all-button"
                  onClick={() => handleTabChange("users")}
                >
                  Xem tất cả
                  <svg
                    className="button-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
              <div className="table-container">
                {users.length > 0 ? (
                  <table className="modern-table">
                    <thead>
                      <tr>
                        <th>Người dùng</th>
                        <th>Email</th>
                        <th>Ngày tham gia</th>
                        <th>Trạng thái</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.slice(0, 5).map((user) => (
                        <tr key={user.id}>
                          <td>
                            <div className="user-info">
                              <div className="user-avatar">
                                {(
                                  user.profile?.displayName ||
                                  user.displayName ||
                                  "U"
                                )
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>
                              <div className="user-details">
                                <p className="user-name">
                                  {user.profile?.displayName ||
                                    user.displayName ||
                                    "N/A"}
                                </p>
                                <p className="user-id">
                                  ID: {user.id.substring(0, 8)}...
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="user-email">
                            {user.profile?.email || user.email || "N/A"}
                          </td>
                          <td className="user-date">
                            {formatDate(user.createdAt)}
                          </td>
                          <td>
                            <span
                              className={`status-badge ${
                                user.status === "active" ? "active" : "inactive"
                              }`}
                            >
                              {user.status === "active"
                                ? "Hoạt động"
                                : "Không hoạt động"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty-state">
                    <svg
                      className="empty-icon"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                      />
                    </svg>
                    <p>Chưa có người dùng nào</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="dashboard-card transactions-card-table">
              <div className="card-header">
                <h3 className="card-title">
                  <svg
                    className="title-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                    />
                  </svg>
                  Giao dịch gần đây
                </h3>
                <button
                  className="view-all-button"
                  onClick={() => handleTabChange("payments")}
                >
                  Xem tất cả
                  <svg
                    className="button-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
              <div className="table-container">
                {transactions.length > 0 ? (
                  <table className="modern-table">
                    <thead>
                      <tr>
                        <th>Giao dịch</th>
                        <th>Gói</th>
                        <th>Số tiền</th>
                        <th>Trạng thái</th>
                        <th>Ngày</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.slice(0, 5).map((transaction) => (
                        <tr key={transaction.id}>
                          <td>
                            <div className="transaction-info">
                              <p className="transaction-id">
                                #{transaction.id.substring(0, 8)}...
                              </p>
                              <p className="transaction-user">
                                User: {transaction.userId.substring(0, 8)}...
                              </p>
                            </div>
                          </td>
                          <td className="transaction-item">
                            {transaction.itemName || "N/A"}
                          </td>
                          <td className="transaction-amount">
                            {formatCurrency(transaction.amount)}
                          </td>
                          <td>
                            <span
                              className={`status-badge ${
                                transaction.status === "completed" ||
                                transaction.status === "PAID"
                                  ? "success"
                                  : "warning"
                              }`}
                            >
                              {transaction.status === "completed" ||
                              transaction.status === "PAID"
                                ? "Hoàn thành"
                                : "Đang xử lý"}
                            </span>
                          </td>
                          <td className="transaction-date">
                            {formatDate(transaction.updatedAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty-state">
                    <svg
                      className="empty-icon"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                    <p>Chưa có giao dịch nào</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "users" && <AdminUserManagement />}
      {activeTab === "news" && <AdminNewsGenerator />}
      {activeTab === "api-history" && <AdminApiManagement />}
      {activeTab === "price-analysis" && <AdminPriceAnalysisManagement />}
      {activeTab === "errors" && <AdminErrorManagement />}
      {activeTab === "manus-keys" && <AdminManusKeyManager />}

      {activeTab === "settings" && (
        <div className="admin-content">
          {/* Header */}
          <div className="content-header">
            <div>
              <h2>Cài đặt hệ thống</h2>
              <p>Quản lý cài đặt và cấu hình hệ thống</p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Làm mới
            </button>
          </div>

          {/* Statistics Cards */}
          <div className="stats-grid">
            <div className="stat-card users-card">
              <div className="stat-card-header">
                <div className="stat-card-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div className="stat-card-info">
                  <p className="stat-card-title">Trạng thái hệ thống</p>
                  <p className="stat-card-value">Hoạt động</p>
                </div>
              </div>
            </div>

            <div className="stat-card active-users-card">
              <div className="stat-card-header">
                <div className="stat-card-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <div className="stat-card-info">
                  <p className="stat-card-title">Chế độ bảo trì</p>
                  <p className="stat-card-value">
                    {maintenanceMode ? "Bật" : "Tắt"}
                  </p>
                </div>
              </div>
            </div>

            <div className="stat-card transactions-card">
              <div className="stat-card-header">
                <div className="stat-card-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <div className="stat-card-info">
                  <p className="stat-card-title">Hiệu suất</p>
                  <p className="stat-card-value">98%</p>
                </div>
              </div>
            </div>

            <div className="stat-card revenue-card">
              <div className="stat-card-header">
                <div className="stat-card-icon">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <div className="stat-card-info">
                  <p className="stat-card-title">Bảo mật</p>
                  <p className="stat-card-value">An toàn</p>
                </div>
              </div>
            </div>
          </div>

          {/* Settings Sections */}
          <div className="data-table">
            <div className="table-header">
              <div className="table-title">
                <h3>Cài đặt chính</h3>
              </div>
            </div>

            <div className="table-content" style={{ padding: "1.5rem" }}>
              {/* Maintenance Mode Section */}
              <div className="fanpages-section">
                <h5>Chế độ bảo trì hệ thống</h5>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "1rem",
                    padding: "1rem",
                    backgroundColor: maintenanceMode ? "#fee2e2" : "#f0fdf4",
                    borderRadius: "0.5rem",
                    border: maintenanceMode
                      ? "1px solid #fecaca"
                      : "1px solid #bbf7d0",
                  }}
                >
                  <div>
                    <p style={{ marginBottom: "0.5rem", fontWeight: "500" }}>
                      Trạng thái bảo trì
                    </p>
                    <p
                      style={{
                        fontSize: "0.875rem",
                        color: "#6b7280",
                        marginBottom: "0.75rem",
                        lineHeight: "1.5",
                      }}
                    >
                      Khi bật chế độ bảo trì, người dùng chỉ có thể truy cập
                      trang chủ và trang admin. Tất cả các tính năng khác sẽ bị
                      khóa.
                    </p>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <span
                        className={`status-badge ${
                          maintenanceMode ? "inactive" : "success"
                        }`}
                      >
                        {maintenanceMode
                          ? "Đang bảo trì"
                          : "Hoạt động bình thường"}
                      </span>
                    </div>
                  </div>
                  <button
                    className={`btn ${
                      maintenanceMode ? "btn-primary" : "btn-danger"
                    }`}
                    onClick={async () => {
                      try {
                        const db = getDatabase(app);
                        const newMode = !maintenanceMode;
                        await set(ref(db, "system/maintenanceMode"), newMode);
                        setMaintenanceMode(newMode);
                      } catch (error) {
                        console.error(
                          "Error updating maintenance mode:",
                          error
                        );
                        setError("Có lỗi khi cập nhật trạng thái bảo trì");
                      }
                    }}
                    style={{ minWidth: "180px" }}
                  >
                    {maintenanceMode
                      ? "Tắt chế độ bảo trì"
                      : "Bật chế độ bảo trì"}
                  </button>
                </div>
              </div>

              {/* System Information Section */}
              <div className="fanpages-section">
                <h5>Thông tin hệ thống</h5>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Phiên bản</label>
                    <input
                      type="text"
                      value="v2.1.0"
                      disabled
                      className="form-input disabled"
                    />
                  </div>
                  <div className="form-group">
                    <label>Môi trường</label>
                    <input
                      type="text"
                      value="Production"
                      disabled
                      className="form-input disabled"
                    />
                  </div>
                  <div className="form-group">
                    <label>Cơ sở dữ liệu</label>
                    <input
                      type="text"
                      value="Firebase Realtime Database"
                      disabled
                      className="form-input disabled"
                    />
                  </div>
                  <div className="form-group">
                    <label>Thời gian chạy</label>
                    <input
                      type="text"
                      value="99.8% uptime"
                      disabled
                      className="form-input disabled"
                    />
                  </div>
                </div>
              </div>

              {/* Security Settings Section */}
              <div className="fanpages-section">
                <h5>Cài đặt bảo mật</h5>
                <div
                  style={{
                    display: "grid",
                    gap: "1rem",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "1rem",
                      backgroundColor: "#f9fafb",
                      borderRadius: "0.5rem",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <div>
                      <p style={{ fontWeight: "500", marginBottom: "0.25rem" }}>
                        Xác thực hai yếu tố
                      </p>
                      <p style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                        Tăng cường bảo mật tài khoản admin
                      </p>
                    </div>
                    <span className="status-badge success">Đã bật</span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "1rem",
                      backgroundColor: "#f9fafb",
                      borderRadius: "0.5rem",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <div>
                      <p style={{ fontWeight: "500", marginBottom: "0.25rem" }}>
                        SSL/TLS
                      </p>
                      <p style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                        Mã hóa kết nối HTTPS
                      </p>
                    </div>
                    <span className="status-badge success">Hoạt động</span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "1rem",
                      backgroundColor: "#f9fafb",
                      borderRadius: "0.5rem",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <div>
                      <p style={{ fontWeight: "500", marginBottom: "0.25rem" }}>
                        Firewall
                      </p>
                      <p style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                        Bảo vệ khỏi các cuộc tấn công
                      </p>
                    </div>
                    <span className="status-badge success">Đã bật</span>
                  </div>
                </div>
              </div>

              {/* Additional Settings Section */}
              <div className="fanpages-section">
                <h5>Cài đặt nâng cao</h5>
                <div
                  style={{
                    padding: "1.5rem",
                    backgroundColor: "#f8fafc",
                    borderRadius: "0.5rem",
                    border: "1px solid #e2e8f0",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "3rem",
                      height: "3rem",
                      backgroundColor: "#e2e8f0",
                      borderRadius: "50%",
                      marginBottom: "1rem",
                    }}
                  >
                    <svg
                      style={{
                        width: "1.5rem",
                        height: "1.5rem",
                        color: "#64748b",
                      }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
                      />
                    </svg>
                  </div>
                  <h6 style={{ fontWeight: "500", marginBottom: "0.5rem" }}>
                    Đang phát triển
                  </h6>
                  <p style={{ color: "#64748b", margin: 0 }}>
                    Các tính năng cài đặt nâng cao đang được phát triển và sẽ
                    được cập nhật trong phiên bản tiếp theo.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
